import { useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import UploadIcon from '@mui/icons-material/Upload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import * as XLSX from 'xlsx';
import CardBox from 'lib/components/CardBox';
import { IElement } from 'lib/common/types';
import { ICompany } from 'modules/requests/types';
import {
  BulkTemplateParams,
  BulkUploadResult,
  RequestApi,
} from 'lib/api/requests';
import { brand } from 'themes/brand';

interface BulkRequestFormProps {
  company?: ICompany;
  sectors?: IElement[];
  instruments?: IElement[];
}

interface SelectedField {
  id: string;
  name: string;
}

const REGION_OPTIONS = [
  { id: 'NORTE', name: 'Norte' },
  { id: 'NORDESTE', name: 'Nordeste' },
  { id: 'CENTRO-OESTE', name: 'Centro-Oeste' },
  { id: 'SUDESTE', name: 'Sudeste' },
  { id: 'SUL', name: 'Sul' },
  { id: 'BRASIL', name: 'Brasil' },
];

// A requisição de download usa `responseType: 'blob'`, então em caso de erro o axios
// também entrega `err.response.data` como Blob (não como JSON já parseado) — é preciso
// ler o texto do blob manualmente para extrair a mensagem que o backend enviou.
const extractErrorMessage = async (
  err: any,
  fallback: string,
): Promise<string> => {
  const data = err?.response?.data;

  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text);
      return parsed?.message ?? fallback;
    } catch {
      return fallback;
    }
  }

  return data?.message ?? fallback;
};

const StepBadge = ({ number }: { number: number }) => (
  <Box
    sx={{
      width: 28,
      height: 28,
      borderRadius: '50%',
      backgroundColor: brand.primaryDark,
      color: '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 700,
      fontSize: 14,
      flexShrink: 0,
    }}
  >
    {number}
  </Box>
);

const StepHeader = ({ number, title }: { number: number; title: string }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
    <StepBadge number={number} />
    <Typography fontWeight={600} fontSize={15}>
      {title}
    </Typography>
  </Box>
);

export function BulkRequestForm({
  company,
  sectors,
  instruments,
}: BulkRequestFormProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [jobTitle, setJobTitle] = useState<SelectedField | null>(null);
  const [instrument, setInstrument] = useState<SelectedField | null>(null);
  const [region, setRegion] = useState<SelectedField | null>(null);
  const [sector, setSector] = useState<SelectedField | null>(null);

  const [uploadVisible, setUploadVisible] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [rowCount, setRowCount] = useState<number | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<BulkUploadResult | null>(null);

  const allFieldsFilled = !!(jobTitle && instrument && region && sector);

  const sortedJobTitles = company
    ? [...(company.jobTitles ?? [])].sort((a, b) =>
        a.name.localeCompare(b.name, 'pt-BR'),
      )
    : [];

  const sortedInstruments = instruments
    ? [...instruments].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
    : [];

  const sortedSectors = sectors
    ? [...sectors].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
    : [];

  const handleDownload = async () => {
    if (!jobTitle || !instrument || !region || !sector) return;
    const params: BulkTemplateParams = {
      jobTitleId: jobTitle.id,
      jobTitleName: jobTitle.name,
      instrumentId: instrument.id,
      instrumentName: instrument.name,
      regionValue: region.id,
      sectorId: sector.id,
      sectorName: sector.name,
    };
    try {
      const blob = await RequestApi.downloadBulkTemplate(params);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().slice(0, 10);
      link.download = `template-solicitacoes-${today}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
      setUploadVisible(true);
    } catch (err: any) {
      const reason = await extractErrorMessage(
        err,
        'Erro ao baixar o template. Tente novamente.',
      );
      setResult({ successCount: 0, errors: [{ candidateName: '—', reason }] });
      setUploadVisible(true);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadedFile(file);
    setResult(null);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = event.target?.result;
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
        }) as unknown[][];
        const dataRows = rows
          .slice(1)
          .filter((r) =>
            [r[0], r[1], r[2]].some(
              (cell) => cell !== null && cell !== undefined && cell !== '',
            ),
          );
        const count = dataRows.length;
        setRowCount(count);
      } catch {
        setRowCount(null);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setRowCount(null);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async () => {
    if (!uploadedFile) return;
    setIsSubmitting(true);
    try {
      const res = await RequestApi.uploadBulkRequests(uploadedFile);
      setResult(res);
    } catch (err: any) {
      const reason =
        err?.response?.data?.message ??
        'Erro ao processar a planilha. Verifique o arquivo e tente novamente.';
      setResult({ successCount: 0, errors: [{ candidateName: '—', reason }] });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewBatch = () => {
    setUploadedFile(null);
    setRowCount(null);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleGoBack = () => {
    setResult(null);
    setUploadedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setUploadVisible(false);
    setJobTitle(null);
    setInstrument(null);
    setRegion(null);
    setSector(null);
  };

  return (
    <CardBox>
      <Typography variant="h6" fontWeight={700} mb={0.5}>
        Solicitar avaliações em massa
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Selecione os campos comuns a todas as solicitações, baixe o template já
        pré-preenchido, complete com os dados de até{' '}
        <Typography component="span" variant="body2" fontWeight={700}>
          50 candidatos
        </Typography>{' '}
        e faça o upload do arquivo.
      </Typography>

      {/* STEP 1 */}
      <StepHeader number={1} title="Defina os campos pré-preenchidos" />
      <Box
        sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}
      >
        <Autocomplete
          options={sortedJobTitles}
          getOptionLabel={(o) => o.name}
          value={jobTitle}
          onChange={(_, v) => setJobTitle(v)}
          renderInput={(params) => (
            <TextField {...params} label="Cargo *" size="small" />
          )}
        />
        <Autocomplete
          options={sortedInstruments}
          getOptionLabel={(o) => o.name}
          value={instrument}
          onChange={(_, v) => setInstrument(v)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Instrumento a ser enviado *"
              size="small"
            />
          )}
        />
        <Autocomplete
          options={REGION_OPTIONS}
          getOptionLabel={(o) => o.name}
          value={region}
          onChange={(_, v) => setRegion(v)}
          renderInput={(params) => (
            <TextField {...params} label="Região *" size="small" />
          )}
        />
        <Autocomplete
          options={sortedSectors}
          getOptionLabel={(o) => o.name}
          value={sector}
          onChange={(_, v) => setSector(v)}
          renderInput={(params) => (
            <TextField {...params} label="Setor *" size="small" />
          )}
        />
      </Box>

      {/* STEP 2 */}
      <StepHeader number={2} title="Baixe o template da planilha" />
      <Box
        sx={{
          border: '1px solid #e0e0e0',
          borderRadius: 2,
          p: 2.5,
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <ArticleOutlinedIcon sx={{ color: brand.primary, fontSize: 22 }} />
          <Typography variant="body2" color="text.secondary">
            Template Excel (.xlsx) com Cargo, Instrumento, Região e Setor já
            preenchidos.
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<FileDownloadIcon />}
          disabled={!allFieldsFilled}
          onClick={handleDownload}
          sx={{
            mb: allFieldsFilled ? 0 : 0.5,
            textTransform: 'uppercase',
            fontWeight: 700,
            letterSpacing: 1,
          }}
        >
          Baixar Template
        </Button>

        {!allFieldsFilled && (
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            mt={0.5}
          >
            Preencha os quatro campos acima para liberar o download.
          </Typography>
        )}

        <Box
          component="a"
          onClick={() => setUploadVisible(true)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            mt: 1.5,
            color: brand.primary,
            fontSize: 14,
            cursor: 'pointer',
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          <UploadIcon sx={{ fontSize: 16 }} />
          Já tenho o template preenchido — ir direto para o upload
        </Box>
      </Box>

      {/* STEP 3 */}
      {uploadVisible && (
        <>
          <StepHeader number={3} title="Suba a planilha preenchida" />

          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />

          {!uploadedFile ? (
            <Box
              onClick={() => fileInputRef.current?.click()}
              sx={{
                border: '2px dashed #c7d5e8',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: brand.tint,
                mb: 2,
                '&:hover': {
                  borderColor: brand.primary,
                  backgroundColor: '#eef3ff',
                },
              }}
            >
              <UploadIcon sx={{ fontSize: 36, color: brand.primary, mb: 1 }} />
              <Typography fontWeight={600} mb={0.5}>
                Clique para selecionar a planilha (.xlsx)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Use o template baixado acima — não altere os cabeçalhos.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                border: '1px solid #c7d5e8',
                borderRadius: 2,
                p: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mb: 2,
                backgroundColor: brand.tint,
              }}
            >
              <CheckCircleIcon sx={{ color: brand.primary, fontSize: 22 }} />
              <Box flex={1}>
                <Typography fontWeight={600} fontSize={14}>
                  {uploadedFile.name}
                </Typography>
                {rowCount !== null && (
                  <Typography variant="caption" color="text.secondary">
                    {rowCount} solicitação(ões) detectada(s).
                  </Typography>
                )}
              </Box>
              <Box
                component="span"
                onClick={handleRemoveFile}
                sx={{
                  cursor: 'pointer',
                  color: '#666',
                  '&:hover': { color: '#333' },
                }}
              >
                <CloseIcon fontSize="small" />
              </Box>
            </Box>
          )}

          {/* Result panel */}
          {result && (
            <Box
              sx={{
                border: `1px solid ${
                  result.errors.length > 0 ? '#f5c6c6' : '#b3d4f5'
                }`,
                borderRadius: 2,
                p: 2.5,
                mb: 2,
                backgroundColor:
                  result.errors.length > 0 ? '#fff5f5' : brand.tint,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  mb: result.errors.length > 0 ? 2 : 0,
                }}
              >
                {result.errors.length === 0 ? (
                  <CheckCircleOutlineIcon
                    sx={{ color: brand.primary, mt: 0.2 }}
                  />
                ) : (
                  <ErrorOutlineIcon sx={{ color: '#d32f2f', mt: 0.2 }} />
                )}
                <Box>
                  <Typography fontWeight={600} fontSize={14}>
                    {result.errors.length === 0
                      ? `${result.successCount} solicitação(ões) criada(s) com sucesso`
                      : `${result.successCount} solicitação(ões) criada(s) com sucesso e ${result.errors.length} solicitação(ões) com erro`}
                  </Typography>
                  {result.errors.length > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      Revise os erros abaixo, corrija na planilha e reenvie
                      apenas as solicitações com falha.
                    </Typography>
                  )}
                </Box>
              </Box>

              {result.errors.length > 0 && (
                <Box
                  component="table"
                  sx={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: 13,
                    mt: 1,
                  }}
                >
                  <Box component="thead">
                    <Box component="tr">
                      <Box
                        component="th"
                        sx={{
                          textAlign: 'left',
                          pb: 1,
                          pt: 0.5,
                          color: '#888',
                          fontWeight: 600,
                          fontSize: 11,
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          width: '40%',
                          borderBottom: '1px solid #eee',
                        }}
                      >
                        Candidato
                      </Box>
                      <Box
                        component="th"
                        sx={{
                          textAlign: 'left',
                          pb: 1,
                          pt: 0.5,
                          color: '#888',
                          fontWeight: 600,
                          fontSize: 11,
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          borderBottom: '1px solid #eee',
                        }}
                      >
                        Motivo do Erro
                      </Box>
                    </Box>
                  </Box>
                  <Box component="tbody">
                    {result.errors.map((err, i) => (
                      <Box component="tr" key={i}>
                        <Box
                          component="td"
                          sx={{
                            py: 1,
                            fontWeight: 600,
                            borderBottom: '1px solid #f0f0f0',
                            pr: 2,
                          }}
                        >
                          {err.candidateName}
                        </Box>
                        <Box
                          component="td"
                          sx={{
                            py: 1,
                            color: '#555',
                            borderBottom: '1px solid #f0f0f0',
                          }}
                        >
                          {err.reason}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* Action buttons */}
          {result ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 1,
              }}
            >
              <Box
                component="a"
                onClick={handleGoBack}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: '#555',
                  fontSize: 14,
                  cursor: 'pointer',
                  textDecoration: 'none',
                  '&:hover': { color: brand.primary },
                }}
              >
                <ArrowBackIcon fontSize="small" />
                Voltar e baixar um novo template
              </Box>
              <Button
                variant="outlined"
                onClick={handleNewBatch}
                sx={{
                  textTransform: 'uppercase',
                  fontWeight: 700,
                  letterSpacing: 1,
                }}
              >
                Enviar nova remessa
              </Button>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                mt: 2,
              }}
            >
              {rowCount !== null && rowCount > 50 && (
                <Typography variant="caption" color="error" fontWeight={600}>
                  A planilha contém {rowCount} solicitações. O limite é de 50
                  por envio. Remova as linhas excedentes antes de continuar.
                </Typography>
              )}
              <Button
                variant="contained"
                disabled={
                  !uploadedFile ||
                  isSubmitting ||
                  (rowCount !== null && rowCount > 50)
                }
                onClick={handleSubmit}
                sx={{
                  textTransform: 'uppercase',
                  fontWeight: 700,
                  letterSpacing: 1,
                  minWidth: 280,
                }}
                startIcon={
                  isSubmitting ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : undefined
                }
              >
                {isSubmitting
                  ? 'Enviando...'
                  : 'Finalizar e enviar solicitações'}
              </Button>
            </Box>
          )}
        </>
      )}

      {/* Submit button when step 3 is not yet visible */}
      {!uploadVisible && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button
            variant="contained"
            disabled
            sx={{
              textTransform: 'uppercase',
              fontWeight: 700,
              letterSpacing: 1,
              minWidth: 280,
            }}
          >
            Finalizar e enviar solicitações
          </Button>
        </Box>
      )}
    </CardBox>
  );
}
